import {HttpClient} from '@angular/common/http'
import {inject, Injectable, signal} from '@angular/core'
import {BehaviorSubject, map, Observable, tap} from 'rxjs'
import {switchMap} from 'rxjs/operators'
import {environment} from '../../environments/environment'
import {getCurrentDownPaymentPercent} from '../application/app'
import {InterestService} from './interest.service'


// I do not really understand why it complains about this. /Daniel 2020-11-23

export enum PropertyType {
  NONE = -1,
  VILLA = 0,
  FRITIDSHUS,
  BOSTADSRATT
}


/**
 * A type for the received hemnet data.
 */
export interface IHemnetData {
  /**
   * The original hemnet objectId
   */
  hemnetId?: string

  /**
   * Sparbanken internal Id for the object
   */
  id?: string

  /**
   * The estimated price, or "utgångspris"
   * Could be a value edited by the user.
   */
  askingPrice: number

  /**
   * Expected price, what the user thinks it's going to be.
   */
  expectedPrice: number

  /**
   * Donw payment, the "cash" to deduct from the price/expected price
   */
  downPayment: number

  /**
   * Like villa, bostadsrätt, fritidshus.
   */
  type?: PropertyType

  /**
   * If property type = bostadsrätt this is mandatory.
   */
  fee: number

  /**
   * Run cost, per year as reported, or entered by the user.
   */
  runCost?: number

  /**
   * Interest rate, text and itemId collected from RT Backend
   */
  interestRate: number
  disclaimerText: string[]
  itemId: string

  /**
   * Living area
   */
  area: number

  /**
   * Year of construction
   */
  year: number

  /**
   * Name
   */
  name: string

  /**
   * An image, actually an Image URL
   */
  image?: string
}

@Injectable({
  providedIn: 'root'
})
export class HemnetService {
  public testMode = signal(false)

  /**
   * Subscribe to this to get the latest info.
   * Publish here when you have updated data.
   * Publishers should make sure to send the complete, last received object.
   */
  public hemnetOutput: BehaviorSubject<IHemnetData> = new BehaviorSubject<IHemnetData>({} as any)

  public hemnetData: BehaviorSubject<IHemnetData> = new BehaviorSubject<IHemnetData>({} as any)

  /**
   * Variable to temporarily use iHemnetData
   */
  public iHemnetData: IHemnetData = {} as any

  private readonly interestService = inject(InterestService)
  private readonly httpClient = inject(HttpClient)

  /**
   * Creates a cookie on the last two parts of the domain.
   */
  static CreateCookie(hemnetData: IHemnetData): void {
    const domain = window.location.hostname.split('.')
    domain.shift()
    const domainName = domain.join('.')
    const id = hemnetData.id
    const dateNumber = new Date().getTime() + 60 * 60 * 1000 // 1 hour
    const date = new Date(dateNumber)
    document.cookie = `hemnet=${id};SameSite=None;Secure=true;Domain=${domainName};Expires=${date.toUTCString()}`
  }

  /**
   * Need this to update form when test mode changes. Could be removed after 2026-04-01
   */
  public currentDownPaymentPercent = () =>
    getCurrentDownPaymentPercent(this.testMode())
  public toggleTestMode = () => this.testMode.update(v => !v)

  /**
   * Fetch the hemnet data from our backend and publish the result.
   */
  public getHemnetData(objectId: string): void {
    const url = `${environment.hemnetServiceUrl}/hemnet/${objectId}`
    const propertiesToConvert = ['downPayment', 'expectedPrice', 'runCost', 'fee', 'year']

    this.httpClient.get<IHemnetData>(url).pipe(
      tap((data: IHemnetData) => {
        // Convert props
        propertiesToConvert.forEach(prop => data[prop] = Number(data[prop]))
        this.iHemnetData = data

        // Publish immediately in case interest lookup fails
        this.hemnetOutput.next(data)
      }),
      switchMap(() => this.interestService.getLatestInterest()),
      map(interest => {
        this.iHemnetData.interestRate = Number(interest.averageRate) || 0
        this.iHemnetData.itemId = interest.itemId

        this.iHemnetData.disclaimerText = interest.disclaimerText || []
        return this.iHemnetData
      })
    ).subscribe(updatedData => this.hemnetOutput.next(updatedData))
  }


  /**
   * Saves hemnet data on server
   */
  public saveHemnetData(hemnetData: IHemnetData): Observable<IHemnetData> {
    const url = `${environment.hemnetServiceUrl}/loanpromise`
    return this.httpClient.put<IHemnetData>(url, hemnetData)
  }
}
